import React from 'react';
import clsx from 'clsx';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /**
   * The size of the checkbox
   */
  size?: 'sm' | 'base' | 'lg';
  
  /**
   * Whether the checkbox should take the full width of its container
   */
  fullWidth?: boolean;
  
  /**
   * Whether the checkbox is in an error state
   */
  error?: boolean;
  
  /**
   * Whether the checkbox is in a success state
   */
  success?: boolean;
  
  /**
   * Label for the checkbox
   */
  label?: string;
  
  /**
   * Helper text to display below the checkbox
   */
  helperText?: string;
  
  /**
   * Whether the checkbox is checked
   */
  checked?: boolean;
  
  /**
   * Whether the checkbox is in an indeterminate state
   */
  indeterminate?: boolean;
}

/**
 * Checkbox component with retro styling and skewed appearance
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      size = 'base',
      fullWidth = false,
      error = false,
      success = false,
      label,
      helperText,
      disabled,
      checked,
      indeterminate = false,
      className,
      ...props
    },
    ref
  ) => {
    const checkboxId = React.useId();
    const checkboxRef = React.useRef<HTMLInputElement>(null);
    
    // Handle indeterminate state
    React.useEffect(() => {
      const checkbox = checkboxRef.current || (ref as React.RefObject<HTMLInputElement>)?.current;
      if (checkbox) {
        checkbox.indeterminate = indeterminate;
      }
    }, [indeterminate, ref]);
    
    return (
      <div
        className={clsx(
          'prometheus-checkbox-wrapper',
          {
            'prometheus-checkbox-wrapper--full-width': fullWidth,
            'prometheus-checkbox-wrapper--disabled': disabled,
            'prometheus-checkbox-wrapper--error': error,
            'prometheus-checkbox-wrapper--success': success,
          }
        )}
      >
        <div className="prometheus-checkbox__container">
          <div className="prometheus-checkbox__input-container">
            <input
              ref={ref || checkboxRef}
              id={checkboxId}
              type="checkbox"
              className={clsx(
                'prometheus-checkbox',
                `prometheus-checkbox--${size}`,
                {
                  'prometheus-checkbox--error': error,
                  'prometheus-checkbox--success': success,
                  'prometheus-checkbox--disabled': disabled,
                  'prometheus-checkbox--indeterminate': indeterminate,
                },
                className
              )}
              disabled={disabled}
              checked={checked}
              {...props}
            />
            
            <div className="prometheus-checkbox__custom">
              <div className="prometheus-checkbox__checkmark">
                {indeterminate ? (
                  <svg
                    className="prometheus-checkbox__indeterminate-icon"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <rect x="3" y="7" width="10" height="2" />
                  </svg>
                ) : (
                  <svg
                    className="prometheus-checkbox__check-icon"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                  </svg>
                )}
              </div>
            </div>
          </div>
          
          {label && (
            <label
              htmlFor={checkboxId}
              className="prometheus-checkbox__label"
            >
              {label}
            </label>
          )}
        </div>
        
        {helperText && (
          <span
            className={clsx(
              'prometheus-checkbox__helper-text',
              {
                'prometheus-checkbox__helper-text--error': error,
                'prometheus-checkbox__helper-text--success': success,
              }
            )}
          >
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;